import Link, { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { FC, ReactNode, useMemo } from 'react';

interface HeaderMenuLinkProps extends LinkProps {
  href: string;
  children: ReactNode;
  className?: string;
  delinkOnEmpty?: boolean;
  target?: string;
}

const HeaderMenuLink: FC<HeaderMenuLinkProps> = ({
  href,
  children,
  className,
  delinkOnEmpty = false,
  ...res
}) => {
  const currentPath = useRouter().asPath.split('#')[0];

  const url = useMemo(() => {
    if (!href || typeof href !== 'string') {
      if (delinkOnEmpty) {
        return null;
      }
      // In case the href is invalid, just hop back to the homepage.
      return '/';
    }

    // If it's an anchor link, let's make sure to replace any anchors currently
    // in the URL.
    if (href.startsWith('#')) {
      const beforeAnchor = currentPath.split('#')[0];
      return `${beforeAnchor}${href}`;
    }

    // External link shouldn't be messed with.
    if (href.startsWith('http')) {
      return href;
    }

    // Convert double slashes ("something//else") to single.
    let normalizedHref = href.replace('//', '/');

    // Make sure there's a leading slash.
    if (!normalizedHref.startsWith('/')) {
      normalizedHref = `/${normalizedHref}`;
    }

    return normalizedHref;
  }, [currentPath, href, delinkOnEmpty]);

  if (url === null) {
    return <div className={className}>{children}</div>;
  }

  const ariaLabelNewTab =
    typeof children === 'string' ? `${children} (Open in a new tab)` : 'Open in a new tab';

  return (
    <Link href={url} className={className} aria-label={ariaLabelNewTab} {...res}>
      {children}
    </Link>
  );
};

export default HeaderMenuLink;
